import { useLookbookWishlist } from '@app/services/hooks/lookbook/useLookbookWishlist';
import { useUpdateLookbookWishlist } from '@app/services/hooks/lookbook/useUpdateLookbookWishlist';
import { useLocalStorage } from '@app/utils/hooks/useLocalStorage';
import { isDefined } from '@app/utils/is';
import React from 'react';

import { useLookbookContext } from './LookbookContext';

const LOOKBOOK_WHISHLIST_PRODUCTS_KEY = 'LOOKBOOK_WHISHLIST_PRODUCTS_KEY';

interface Context {
  toggleProductInWishlist: (productId: string) => void;
  clearWishlist: VoidFunction;
  resetLocalWishlist: VoidFunction;
  wishlist: Set<string>;
  wishlistArray: string[];
  wishlistId: string | undefined;
}

export const useLookbookWishlistContext = (): Context => {
  const context = React.useContext(LookbookWishlistContext);
  if (context === null) {
    throw new Error('LookbookWishlistContext can not be used outside the scope of LookbookWishlistContextProvider');
  }

  return context;
};
const LookbookWishlistContext = React.createContext<Context | null>(null);
interface LookbookWishlistContextProviderProps {
  wishlistId: string | undefined;
}

interface LocalWishlist {
  lookbookId: string;
  products: string[];
}

export const LookbookWishlistContextProvider = ({ children, wishlistId }: React.WithChildren<LookbookWishlistContextProviderProps>) => {
  const { lookbookId } = useLookbookContext();
  const [storedWishlist, setStoredWishlist] = useLocalStorage<LocalWishlist>({
    asJson: true,
    defaultValue: { lookbookId, products: [] },
    key: LOOKBOOK_WHISHLIST_PRODUCTS_KEY,
  });
  const localWishlist = React.useMemo(() => new Set(storedWishlist.products), [storedWishlist]);
  const { data: wishlistData } = useLookbookWishlist({ lookbookId, wishlistId });
  const { mutate } = useUpdateLookbookWishlist();
  const remoteWishlist = React.useMemo(() => new Set(wishlistData?.products ?? []), [wishlistData]);
  const wishlist = React.useMemo(
    () => (isDefined(wishlistId) ? remoteWishlist : localWishlist),
    [localWishlist, remoteWishlist, wishlistId],
  );
  const wishlistArray = React.useMemo(() => [...wishlist], [wishlist]);
  const resetLocalWishlist = React.useCallback(() => {
    setStoredWishlist({ lookbookId, products: [] });
  }, [lookbookId, setStoredWishlist]);

  const clearWishlist = () => {
    if (isDefined(wishlistId)) {
      mutate({ data: { products: [] }, lookbookId, wishlistId });

      return;
    }
    setStoredWishlist({ lookbookId, products: [] });
  };

  const toggleProductInWishlist = (id: string) => {
    const whislistCopy = new Set(wishlist);

    if (whislistCopy.has(id)) {
      whislistCopy.delete(id);
    } else {
      whislistCopy.add(id);
    }

    if (isDefined(wishlistId)) {
      mutate({ data: { products: [...whislistCopy] }, lookbookId, wishlistId });

      return;
    }

    setStoredWishlist({ ...storedWishlist, products: [...whislistCopy] });
  };

  React.useEffect(() => {
    if (lookbookId !== storedWishlist.lookbookId) {
      resetLocalWishlist();
    }
  }, []);

  return (
    <LookbookWishlistContext.Provider
      value={{
        clearWishlist,
        resetLocalWishlist,
        toggleProductInWishlist,
        wishlist,
        wishlistArray,
        wishlistId,
      }}
    >
      {children}
    </LookbookWishlistContext.Provider>
  );
};
